import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook } from "@fortawesome/free-brands-svg-icons/faFacebook";
import { faInstagram } from "@fortawesome/free-brands-svg-icons/faInstagram";
import { faXTwitter } from "@fortawesome/free-brands-svg-icons/faXTwitter";
import { faYoutube } from "@fortawesome/free-brands-svg-icons/faYoutube";
import { faTiktok } from "@fortawesome/free-brands-svg-icons/faTiktok";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons/faLinkedin";
import type { SocialLinks } from "@wondermaps/db/schema";
import { getContrastTextColor } from "@wondermaps/maps";
import { useTranslation } from "react-i18next";
import { analytics } from "../../lib/analytics";

export interface SocialLinksRowProps {
  socialLinks: SocialLinks;
  /** When set, render branded mode: each icon in a circle bg = brandedColor, glyph color flips by contrast */
  brandedColor?: string | null;
}

const PLATFORMS = [
  { key: "facebook" as const, icon: faFacebook, label: "Facebook" },
  { key: "instagram" as const, icon: faInstagram, label: "Instagram" },
  { key: "twitter" as const, icon: faXTwitter, label: "X (Twitter)" },
  { key: "youtube" as const, icon: faYoutube, label: "YouTube" },
  { key: "tiktok" as const, icon: faTiktok, label: "TikTok" },
  { key: "linkedin" as const, icon: faLinkedin, label: "LinkedIn" },
];

export function SocialLinksRow({ socialLinks, brandedColor }: SocialLinksRowProps) {
  const { t } = useTranslation("common");
  const activePlatforms = PLATFORMS.filter(
    (platform) => socialLinks[platform.key] != null,
  );

  if (activePlatforms.length === 0) {
    return null;
  }

  const branded = !!(brandedColor && brandedColor.startsWith("#"));
  const glyphColor = branded ? getContrastTextColor(brandedColor!) : undefined;

  return (
    <div
      className="flex items-center justify-center flex-wrap gap-2 py-3 px-4"
      aria-label={t("socialLinks")}
    >
      {activePlatforms.map((platform) => (
        <a
          key={platform.key}
          href={socialLinks[platform.key]!}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={platform.label}
          className={
            branded
              ? "min-h-[44px] min-w-[44px] flex items-center justify-center transition-opacity hover:opacity-80"
              : "p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          }
          onClick={() => {
            analytics.clickOutbound({
              url: socialLinks[platform.key]!,
              label: platform.label,
              location: "social_links",
            });
          }}
        >
          {branded ? (
            <span
              aria-hidden
              className="rounded-full w-10 h-10 flex items-center justify-center"
              style={{ backgroundColor: brandedColor!, color: glyphColor }}
            >
              <FontAwesomeIcon icon={platform.icon} className="w-5 h-5" />
            </span>
          ) : (
            <FontAwesomeIcon icon={platform.icon} className="w-5 h-5" />
          )}
        </a>
      ))}
    </div>
  );
}
